import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { IRecordFurtherEducationGroupEditItem } from "source/models/recordFurtherEducationGroup/recordFurtherEducationGroupEditItem";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import * as recordService from "source/models/services/recordService";
import * as storeActions from "source/models/store/storeActions";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormSelect } from "source/components/framework/FormSelect";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IRecordFurtherEducationGroupDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    canBeRemoved: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshTree: boolean) => void;
    onRemove: (id: string) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IRecordFurtherEducationGroupDialogState {
    item: IRecordFurtherEducationGroupEditItem;
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    requiredQualificationCount: number;
}

export class RecordFurtherEducationGroupDialog extends React.Component<IRecordFurtherEducationGroupDialogProp, IRecordFurtherEducationGroupDialogState> {
    private orgStateHash: string = "";

    constructor(props: IRecordFurtherEducationGroupDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            requiredQualificationCount: 0,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => recordService.getRecordFurtherEducationGroupEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.educationGroup;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            requiredQualificationCount: item.requiredQualificationCount
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IRecordFurtherEducationGroupDialogProp, prevState: IRecordFurtherEducationGroupDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    getSaveDataFromState = (props: IRecordFurtherEducationGroupDialogProp, state: IRecordFurtherEducationGroupDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("requiredQualificationCount", state.requiredQualificationCount ?? 0);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => recordService.saveRecordFurtherEducationGroupEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordFurtherEducationGroupParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove(state.id);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="record-further-education-group-dialog"
                title={state.isNew
                    ? ((props.companyId ? Translations.AddFurtherEducationGroup : Translations.AddDefaultFurtherEducationGroup) + " / " + Translations.CompetencyGroup)
                    : ((props.companyId ? Translations.FurtherEducationGroup : Translations.DefaultFurtherEducationGroup) + " / " + Translations.CompetencyGroup + ": " + state.item.code)}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField type="text" name="code" title={Translations.Name} inputProps={{ maxLength: 100 }} disabled={false} value={state.code} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.RequiredValidQualificationCount}>
                                    <FormSelect
                                        value={state.requiredQualificationCount.toString(10)}
                                        tooltip={Translations.RequiredValidQualificationCount}
                                        items={[{ value: "0", title: "'" + Translations.NotInUse + "'" }].concat(["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"].map(i => ({ value: i, title: i })))}
                                        onChange={(requiredQualificationCount) => this.setState({ requiredQualificationCount: requiredQualificationCount.toInteger() })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Description}>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}